import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Typography,
	Stack,
	Card,
	InputAdornment,
	GlobalStyles,
	ToggleButton,
	ToggleButtonGroup,
	Alert,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { StyledTextField } from "../../components/ui/CommonReusables";
import { footerPagerSx, toggleGroupSx } from "../CurriculumManagementAdmin/components/buttonStyles";
import { stripHtml } from "../CurriculumManagementAdmin/utils/html";
import AttendanceCalendar from "../CurriculumManagementAdmin/components/AttendanceCalendar";
import { countAttendedByCurriculum, decorateTrainings, deriveAttendedTrainings } from "./data/agentCurriculum";
import CurriculumAPI from "../../api/CurriculumAPI";
import { getCurriculumColumns } from "./CurriculumColumns";
import { getAttendedColumns } from "./AttendedColumns";
import AgentCalendarEntry from "./components/AgentCalendarEntry";
import CurriculumPreviewDialog from "./components/CurriculumPreviewDialog";
import TrainingDetailsDialog from "./components/TrainingDetailsDialog";
import EmptyState from "./components/EmptyState";

const MOUNT_ID = "ahg-curriculum-enrollments";

// Elementor constrains the shortcode's ancestor containers to a boxed max-width, which squeezes
// the page into half the viewport. :has() scopes the release to only the chain wrapping our mount
// node, so the sidebar and every other widget on the page keep their own widths.
const ELEMENTOR_WRAPPERS = [
	".elementor-widget-shortcode",
	".elementor-widget-container",
	".elementor-container",
	".e-con-inner",
	".e-con",
	".entry-content",
	".site-main",
];

const fullWidthOverrides = {
	[`#${MOUNT_ID}`]: { width: "100%", maxWidth: "100%" },
	[ELEMENTOR_WRAPPERS.map((selector) => `${selector}:has(#${MOUNT_ID})`).join(", ")]: {
		width: "100% !important",
		maxWidth: "100% !important",
		flexBasis: "100% !important",
	},
};

// Search strip sitting directly above a table, sharing its top border.
const TableToolbar = ({ value, onChange, placeholder }) => (
	<Box
		sx={{
			border: "1px solid #D6E6F5",
			borderBottom: "none",
			borderRadius: "8px 8px 0 0",
			px: 2,
			py: 1.5,
			display: "flex",
			justifyContent: "flex-end",
		}}
	>
		<StyledTextField
			size="small"
			placeholder={placeholder}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			sx={{ width: { xs: "100%", sm: 280 } }}
			slotProps={{
				input: {
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
						</InputAdornment>
					),
				},
			}}
		/>
	</Box>
);

// Shared by both tables so they read as one component — and as one with the admin page's tables.
const gridSx = {
	// v8 drives the header/pinned background from this custom property.
	"--DataGrid-containerBackground": "#1D54A2",
	width: "100%",
	borderColor: "#D6E6F5",
	borderRadius: "0 0 8px 8px",
	backgroundColor: "background.paper",

	// Navy header with white labels. Elementor was tinting the label text dark, leaving it
	// unreadable on the navy.
	"& .MuiDataGrid-columnHeader": {
		backgroundColor: "#1D54A2 !important",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 700,
		color: "#FFFFFF !important",
		whiteSpace: "normal",
		lineHeight: 1.2,
	},
	"& .MuiDataGrid-columnSeparator": {
		color: "rgba(255, 255, 255, 0.35)",
	},
	"& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
		color: "#FFFFFF !important",
	},
	"& .MuiDataGrid-cell": {
		borderColor: "#EDF4FB",
		color: "#1A3F7A",
	},
	"& .MuiDataGrid-row:hover": {
		backgroundColor: "#F7FBFF",
	},
	"& .MuiDataGrid-footerContainer": {
		borderColor: "#D6E6F5",
	},

	...footerPagerSx,
};

const CurriculumEnrollments = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [curricula, setCurricula] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");

	const [search, setSearch] = useState("");
	const [attendedSearch, setAttendedSearch] = useState("");
	const [curriculumView, setCurriculumView] = useState("table");
	// Held by id rather than as objects so the dialogs re-read fresh state after each change.
	const [previewId, setPreviewId] = useState(null);
	// Opened from a calendar entry or from the attended table's action — the same session details.
	const [detailsTrainingId, setDetailsTrainingId] = useState(null);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const data = await CurriculumAPI.getMyCurricula();
				if (!cancelled) setCurricula(Array.isArray(data) ? data : []);
			} catch (error) {
				if (!cancelled) setLoadError(error?.message || "Could not load your curriculums.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	// Every training, flattened and carrying this agent's own attendance — the single derivation
	// both sections and the calendar read from.
	const trainings = useMemo(() => decorateTrainings(curricula), [curricula]);
	const attendedByCurriculum = useMemo(() => countAttendedByCurriculum(trainings), [trainings]);
	const attendedRows = useMemo(() => deriveAttendedTrainings(trainings), [trainings]);

	const curriculumColumns = useMemo(() => getCurriculumColumns((row) => setPreviewId(row.id), attendedByCurriculum), [attendedByCurriculum]);
	const attendedColumns = useMemo(() => getAttendedColumns((row) => setDetailsTrainingId(row.id)), []);

	// Matches on the curriculum's own text or any training title under it.
	const curriculumRows = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return curricula;

		return curricula.filter(
			(row) =>
				row.title.toLowerCase().includes(query) ||
				stripHtml(row.description).toLowerCase().includes(query) ||
				row.trainings.some((training) => training.title.toLowerCase().includes(query))
		);
	}, [curricula, search]);

	const filteredAttendedRows = useMemo(() => {
		const query = attendedSearch.trim().toLowerCase();
		if (!query) return attendedRows;

		return attendedRows.filter((row) => row.title.toLowerCase().includes(query) || row.curriculumTitle.toLowerCase().includes(query));
	}, [attendedRows, attendedSearch]);

	const previewCurriculum = curricula.find((row) => row.id === previewId) ?? null;
	const previewTrainings = useMemo(() => trainings.filter((training) => training.curriculumId === previewId), [trainings, previewId]);
	const detailsTraining = trainings.find((training) => training.id === detailsTrainingId) ?? null;

	// Distinguishes "no matches" from "nothing here yet"; memoized so the overlay isn't remounted each render.
	const curriculumEmptyState = useMemo(
		() => () => (
			<EmptyState
				icon={MenuBookIcon}
				title={search.trim() ? "No curriculums match your search" : "No curriculums available"}
				description={search.trim() ? "Try a different keyword." : "Check back later for new curriculums."}
			/>
		),
		[search]
	);

	const attendedEmptyState = useMemo(
		() => () => (
			<EmptyState
				icon={WorkspacePremiumIcon}
				title={attendedSearch.trim() ? "No trainings match your search" : "No training badges yet"}
				description={attendedSearch.trim() ? "Try a different keyword." : "Badges appear here once you're marked as attending a training."}
			/>
		),
		[attendedSearch]
	);

	return (
		<>
			<GlobalStyles styles={fullWidthOverrides} />

			<Box sx={{ p: { xs: 2, md: 3 }, width: "100%", minHeight: "100vh" }}>
				<Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4, mb: 2.5 }}>
					CURRICULUMS &amp; TRAININGS
				</Typography>

				<Card sx={{ p: { xs: 2, md: 3 }, width: "100% !important", backgroundColor: "background.paper" }}>
					{/* Curriculums & Trainings — table or the same sessions plotted on the day they were held */}
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						justifyContent="space-between"
						alignItems={{ xs: "stretch", sm: "flex-start" }}
						sx={{ mb: 2 }}
					>
						<Box>
							<Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Curriculums &amp; Trainings</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								{curriculumView === "table"
									? "Preview a curriculum to see the trainings under it and which ones you attended"
									: "Every training on the day it was held — the green ones are the sessions you attended"}
							</Typography>
						</Box>

						<ToggleButtonGroup
							exclusive
							size="small"
							value={curriculumView}
							onChange={(_event, value) => value && setCurriculumView(value)}
							sx={{ alignSelf: { xs: "flex-start", sm: "center" }, ...toggleGroupSx }}
						>
							<ToggleButton value="table">
								<TableRowsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
								Table
							</ToggleButton>
							<ToggleButton value="calendar">
								<CalendarMonthOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
								Calendar
							</ToggleButton>
						</ToggleButtonGroup>
					</Stack>

					{loadError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{loadError}
						</Alert>
					)}

					{curriculumView === "table" ? (
						<>
							<TableToolbar value={search} onChange={setSearch} placeholder="Search curriculum or training..." />

							<DataGrid
								rows={curriculumRows}
								columns={curriculumColumns}
								loading={loading}
								autoHeight
								rowHeight={64}
								disableColumnMenu
								disableRowSelectionOnClick
								showCellVerticalBorder
								showColumnVerticalBorder
								initialState={{ pagination: { paginationModel: { pageSize: 6, page: 0 } } }}
								pageSizeOptions={[6, 12, 24]}
								slots={{ noRowsOverlay: curriculumEmptyState, noResultsOverlay: curriculumEmptyState }}
								sx={gridSx}
							/>
						</>
					) : (
						<AttendanceCalendar
							trainings={trainings}
							onSelectTraining={(training) => setDetailsTrainingId(training.id)}
							renderEntry={({ training, onSelect }) => <AgentCalendarEntry key={training.id} training={training} onSelect={onSelect} />}
						/>
					)}

					{/* Attended trainings — one row per session, each with the badge it earned */}
					<Box sx={{ mt: 4 }}>
						<Box sx={{ mb: 2 }}>
							<Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Attended Curriculum Trainings</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Every training you were marked as attending, and the badge it earned you
							</Typography>
						</Box>

						<TableToolbar value={attendedSearch} onChange={setAttendedSearch} placeholder="Search training or curriculum..." />

						<DataGrid
							loading={loading}
							rows={filteredAttendedRows}
							columns={attendedColumns}
							autoHeight
							rowHeight={64}
							disableColumnMenu
							disableRowSelectionOnClick
							showCellVerticalBorder
							showColumnVerticalBorder
							initialState={{ pagination: { paginationModel: { pageSize: 6, page: 0 } } }}
							pageSizeOptions={[6, 12, 24]}
							slots={{ noRowsOverlay: attendedEmptyState, noResultsOverlay: attendedEmptyState }}
							sx={gridSx}
						/>
					</Box>
				</Card>
			</Box>

			<CurriculumPreviewDialog
				open={Boolean(previewCurriculum)}
				onClose={() => setPreviewId(null)}
				curriculum={previewCurriculum}
				trainings={previewTrainings}
			/>

			<TrainingDetailsDialog open={Boolean(detailsTraining)} onClose={() => setDetailsTrainingId(null)} training={detailsTraining} />
		</>
	);
};

export default CurriculumEnrollments;
